import { Table, type TableProps } from 'antd';
import type { ColumnFilterItem, FilterDropdownProps } from 'antd/lib/table/interface';
import { isNil, pick } from 'lodash/fp';
import { type Key, useCallback, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import * as permissionKind from '../../../../shared/permissions';
import { type CompanyContextDataFragment } from '../../../api/fragments/CompanyContextData';
import { type FinderVehiclesListDataFragment } from '../../../api/fragments/FinderVehiclesListData';
import { useListFinderVehiclesQuery } from '../../../api/queries/listFinderVehicles';
import {
    BooleanWithEmpty,
    CompanyFilterListCollection,
    type FilterVehicleFilteringRule,
    type FinderVehicleCondition,
    type FinderVehicleFunctionality,
    FinderVehicleSortingField,
    type FinderVehicleStatus,
    SortingOrder,
} from '../../../api/types';
import { useAccount } from '../../../components/contexts/AccountContextManager';
import { useMultipleDealerIds } from '../../../components/contexts/DealerContextManager';
import FilterBox from '../../../components/FilterBox';
import { type PaginatedTableProps } from '../../../components/PaginatedTable';
import PaginatedTableWithContext from '../../../components/PaginatedTableWithContext';
import NotFoundResult from '../../../components/results/NotFoundResult';
import { defaultInitialState, useListReducer } from '../../../components/useListReducer';
import useSearchInDropDown from '../../../components/useSearchInDropDown';
import hasPermissions from '../../../utilities/hasPermissions';
import renderFilterIcon from '../../../utilities/renderFilterIcon';
import renderSearchIcon from '../../../utilities/renderSearchIcon';
import useGetCompanyFilter from '../../../utilities/useGetCompanyFilter';
import useSortAndFilterCache, {
    SortAndFilterCacheKey,
    TableSortingOrder,
} from '../../../utilities/useSortAndFilterCache';
import useTranslatedString from '../../../utilities/useTranslatedString';
import PriceFeatureLabel from '../../shared/PriceFeatureLabel';
import useFinderMapper from './useFinderMapper';
import useFinderOptions from './useFinderOptions';
import useSortOrder, { getSortField } from './useSortOrder';

type FinderVehicleDataSource = FinderVehiclesListDataFragment & { key: Key };

type PaginatedFinderVehicleTable = PaginatedTableProps<FinderVehicleDataSource>;

type FinderVehicleListProps = {
    company: CompanyContextDataFragment;
    companies: CompanyContextDataFragment[];
    modulesHasLta?: boolean;
};
const FinderVehicleList = ({ company, companies, modulesHasLta }: FinderVehicleListProps) => {
    const { t } = useTranslation(['finderVehicleList']);

    const { permissions: accountPermissions } = useAccount();

    let defaultCompanyFilter: string[] = [];
    if (companies.length) {
        defaultCompanyFilter = companies.map(company => company.id);
    } else if (company) {
        defaultCompanyFilter = [company.id];
    }

    // get partner number when single dealer is selected
    const { dealerIds, dealersFromApi } = useMultipleDealerIds();
    const partnerNumber = useMemo(() => {
        if (dealerIds?.length !== 1) {
            return '';
        }

        const dealer = dealersFromApi.find(({ id }) => id === dealerIds[0]);
        if (isNil(dealer)) {
            return '';
        }

        return dealer.integrationDetails.partnerNumber || 'UNKNOWN';
    }, [dealerIds, dealersFromApi]);

    const [currentCache, setCache] = useSortAndFilterCache(SortAndFilterCacheKey.FinderVehicle);

    const [state, dispatch] = useListReducer<FinderVehicleSortingField, FilterVehicleFilteringRule>({
        companyId: currentCache?.companyId,
        sort: currentCache?.sort || {
            field: FinderVehicleSortingField.InventoryId,
            order: SortingOrder.Asc,
            orderValue: TableSortingOrder.Asc,
        },
        page: currentCache?.page || defaultInitialState.page,
        pageSize: currentCache?.pageSize || defaultInitialState.pageSize,
        filter: currentCache?.filter || {
            companyIds: defaultCompanyFilter,
            partnerNumber,
        },
    });

    const stateRef = useRef(state);
    stateRef.current = state;

    // switch partner number when dealer changes
    useEffect(() => {
        if (currentCache?.filter?.partnerNumber !== partnerNumber) {
            dispatch({ type: 'setFilter', filteringRule: { ...stateRef.current.filter, partnerNumber } });
        }
    }, [dispatch, partnerNumber, currentCache?.filter?.partnerNumber]);

    const { filter, page, pageSize, sort } = state;

    const { data, loading } = useListFinderVehiclesQuery({
        fetchPolicy: 'cache-and-network',
        variables: {
            filter: {
                ...filter,
                companyIds: filter?.companyIds?.length ? filter.companyIds : defaultCompanyFilter,
            },
            pagination: { offset: (page - 1) * pageSize, limit: pageSize },
            sort: pick(['field', 'order'], sort),
        },
    });
    const companyFilterList = useGetCompanyFilter({ collection: CompanyFilterListCollection.FinderVehicles });

    const searchBox = useSearchInDropDown();
    const navigate = useNavigate();

    const options = useFinderOptions();
    const { booleanWithEmptyToBoolean, ...mapper } = useFinderMapper();

    const onChange = useCallback<TableProps<FinderVehiclesListDataFragment>['onChange']>(
        (pagination, filters, sorter, extra) => {
            switch (extra.action) {
                case 'sort': {
                    return dispatch({
                        type: 'setSort',
                        sortBy: getSortField(sorter),
                    });
                }

                case 'filter':
                    return dispatch({
                        type: 'setFilter',
                        filteringRule: {
                            companyIds: !filters.company ? undefined : (filters.company as string[]),
                            inventoryId: (!filters['listing.id'] ? null : filters['listing.id'][0]) as string | null,
                            vehicleId: (!filters['listing.vehicle.orderTypeCode']
                                ? null
                                : filters['listing.vehicle.orderTypeCode'][0]) as string | null,
                            name: (!filters.name ? null : filters.name[0]) as string | null,
                            conditions: (!filters['listing.vehicle.condition.value']
                                ? null
                                : filters['listing.vehicle.condition.value']) as (FinderVehicleCondition | null)[],
                            dealerName: (!filters['listing.seller.name.localize']
                                ? null
                                : filters['listing.seller.name.localize'][0]) as string | null,
                            exteriorColor: (!filters['listing.vehicle.exteriorColor.name.localize']
                                ? null
                                : filters['listing.vehicle.exteriorColor.name.localize'][0]) as string | null,
                            functionalities: (!filters['setting.functionality']
                                ? null
                                : filters['setting.functionality']) as (FinderVehicleFunctionality | null)[],
                            hasLta: (!filters.lta ? null : filters.lta) as (BooleanWithEmpty | null)[],
                            inspected: (!filters['setting.isInspected']
                                ? null
                                : filters['setting.isInspected']) as (BooleanWithEmpty | null)[],
                            isStateHidden: !filters['setting.isStateHidden']
                                ? null
                                : filters['setting.isStateHidden'].map(booleanWithEmptyToBoolean),
                            statuses: (!filters.status ? null : filters.status) as (FinderVehicleStatus | null)[],
                        },
                    });

                default:
                    return undefined;
            }
        },
        [dispatch, booleanWithEmptyToBoolean]
    );

    const total = data?.vehicles.count || 0;
    const dataSource = useMemo(() => data?.vehicles.items || [], [data?.vehicles.items]);
    const translated = useTranslatedString();

    const onRow = useCallback<PaginatedFinderVehicleTable['onRow']>(
        record => ({
            onClick: () => {
                setCache(state);
                navigate(record.id);
            },
            style: { cursor: 'pointer' },
        }),
        [navigate, setCache, state]
    );
    const showFilterDropDown = useCallback(
        (props: FilterDropdownProps, filters: ColumnFilterItem[]) => (
            <FilterBox filterDropDownProps={props} filters={filters} />
        ),
        []
    );

    const filterOptions = useMemo(() => {
        const rename =
            <T, R>(currentKey: keyof T, newKey: keyof R) =>
            <K extends T>(items: K[]) =>
                items.map(item => {
                    const { [currentKey]: _, ...rest } = item;

                    return { ...rest, [newKey]: item[currentKey] } as { [key in keyof R]: K[keyof T] } & Omit<
                        K,
                        keyof T
                    >;
                });

        const renameToText = rename('label', 'text');

        return {
            condition: renameToText(options.condition),
            inspected: renameToText(options.inspected),
            lta: renameToText(options.lta),
            functionality: renameToText(options.functionality),
            status: renameToText(options.status),
            isStateHidden: [
                {
                    text: t('common:options.finderVehicle.state.show'),
                    value: BooleanWithEmpty.Off,
                },
                {
                    text: t('common:options.finderVehicle.state.hide'),
                    value: BooleanWithEmpty.On,
                },
            ],
        };
    }, [options.condition, options.functionality, options.inspected, options.lta, options.status, t]);

    const isSingapore = !company || company?.countryCode === 'SG';

    const {
        companySort,
        inventoryIdSort,
        vehicleIdSort,
        nameSort,
        conditionSort,
        dealerNameSort,
        exteriorColorSort,
        priceSort,
        hasLtaSort,
        isStateHiddenSort,
        functionalitySort,
        statusSort,
    } = useSortOrder(sort);

    if (!hasPermissions(accountPermissions, [permissionKind.viewFinderVehicles])) {
        return <NotFoundResult />;
    }

    return (
        <PaginatedTableWithContext
            company={company}
            dataSource={dataSource}
            dispatch={dispatch}
            loading={loading}
            onChange={onChange}
            onRow={onRow}
            rowKey="id"
            scroll={{ x: true }}
            state={state}
            tableName={t('finderVehicleList:title')}
            total={total}
        >
            {!company && (
                <Table.Column
                    key="company"
                    dataIndex={['module', 'company', 'displayName']}
                    filterDropdown={props => showFilterDropDown(props, companyFilterList)}
                    filterIcon={renderFilterIcon}
                    filteredValue={filter.companyIds}
                    sortOrder={companySort}
                    title={t('finderVehicleList:columns.company')}
                    sorter
                />
            )}
            <Table.Column
                dataIndex={['listing', 'id']}
                filterDropdown={searchBox.render(t('finderVehicleList:columns.inventoryID'))}
                filterIcon={renderSearchIcon}
                filteredValue={filter.inventoryId ? [filter.inventoryId] : undefined}
                onFilterDropdownOpenChange={searchBox.autoFocus}
                sortOrder={inventoryIdSort}
                title={t('finderVehicleList:columns.inventoryID')}
                sorter
            />
            <Table.Column
                dataIndex={['listing', 'vehicle', 'orderTypeCode']}
                filterDropdown={searchBox.render(t('finderVehicleList:columns.vehicleID'))}
                filterIcon={renderSearchIcon}
                filteredValue={filter.vehicleId ? [filter.vehicleId] : undefined}
                onFilterDropdownOpenChange={searchBox.autoFocus}
                sortOrder={vehicleIdSort}
                title={t('finderVehicleList:columns.vehicleID')}
                sorter
            />
            <Table.Column
                dataIndex="name"
                filterDropdown={searchBox.render(t('finderVehicleList:columns.name'))}
                filterIcon={renderSearchIcon}
                filteredValue={filter.name ? [filter.name] : undefined}
                onFilterDropdownOpenChange={searchBox.autoFocus}
                render={name => translated(name)}
                sortOrder={nameSort}
                title={t('finderVehicleList:columns.name')}
                sorter
            />
            <Table.Column<FinderVehicleDataSource>
                dataIndex={['listing', 'vehicle', 'condition', 'value']}
                filterDropdown={props => showFilterDropDown(props, filterOptions.condition)}
                filterIcon={renderFilterIcon}
                filteredValue={filter.conditions}
                render={(_, finder) => mapper.conditionLabel(finder)}
                sortOrder={conditionSort}
                title={t('finderVehicleList:columns.condition')}
                sorter
            />
            <Table.Column
                dataIndex={['listing', 'seller', 'name', 'localize']}
                filterDropdown={searchBox.render(t('finderVehicleList:columns.dealer'))}
                filterIcon={renderSearchIcon}
                filteredValue={filter.dealerName ? [filter.dealerName] : undefined}
                onFilterDropdownOpenChange={searchBox.autoFocus}
                sortOrder={dealerNameSort}
                title={t('finderVehicleList:columns.dealer')}
                sorter
            />
            <Table.Column
                dataIndex={['listing', 'vehicle', 'exteriorColor', 'name', 'localize']}
                filterDropdown={searchBox.render(t('finderVehicleList:columns.colour'))}
                filterIcon={renderSearchIcon}
                filteredValue={filter.exteriorColor ? [filter.exteriorColor] : undefined}
                onFilterDropdownOpenChange={searchBox.autoFocus}
                sortOrder={exteriorColorSort}
                title={t('finderVehicleList:columns.colour')}
                sorter
            />
            <Table.Column
                dataIndex={['listing', 'price', 'value']}
                render={(value: number, record: FinderVehicleDataSource) => {
                    if (record.module.__typename !== 'FinderVehicleManagementModule') {
                        return value;
                    }

                    return (
                        <PriceFeatureLabel
                            currency={record.module.company.currency}
                            decimals={record.module.company.roundings.amount.decimals}
                            percentages={record.module.company.roundings.percentage.decimals}
                            value={value}
                        />
                    );
                }}
                sortOrder={priceSort}
                title={t('finderVehicleList:columns.price')}
                sorter
            />
            {isSingapore && modulesHasLta && (
                <Table.Column<FinderVehicleDataSource>
                    dataIndex="lta"
                    filterDropdown={props => showFilterDropDown(props, filterOptions.lta)}
                    filterIcon={renderFilterIcon}
                    filteredValue={filter.hasLta}
                    render={(_, finder) => mapper.ltaLabel(finder)}
                    sortOrder={hasLtaSort}
                    title={t('finderVehicleList:columns.ltaData')}
                    sorter
                />
            )}
            <Table.Column
                dataIndex={['setting', 'isStateHidden']}
                filterDropdown={props => showFilterDropDown(props, filterOptions.isStateHidden)}
                filterIcon={renderFilterIcon}
                filteredValue={filter.isStateHidden}
                render={mapper.stateLabel}
                sortOrder={isStateHiddenSort}
                title={t('finderVehicleList:columns.buttonState')}
                sorter
            />
            <Table.Column
                dataIndex={['setting', 'functionality']}
                filterDropdown={props => showFilterDropDown(props, filterOptions.functionality)}
                filterIcon={renderFilterIcon}
                filteredValue={filter.functionalities}
                render={mapper.functionalityLabel}
                sortOrder={functionalitySort}
                title={t('finderVehicleList:columns.buttonFunction')}
                sorter
            />
            <Table.Column
                dataIndex="status"
                filterDropdown={props => showFilterDropDown(props, filterOptions.status)}
                filterIcon={renderFilterIcon}
                filteredValue={filter.statuses}
                render={mapper.statusLabel}
                sortOrder={statusSort}
                title={t('finderVehicleList:columns.status')}
                sorter
            />
        </PaginatedTableWithContext>
    );
};

export default FinderVehicleList;
